import { createFileRoute, Link } from "@tanstack/react-router";
import { PageHero } from "@/components/page-hero";
import karaokeHeroAsset from "@/assets/location-karaoke-cavaillon-soiree-privee.webp.asset.json";
import { metaDesc } from "@/lib/seo-description";

import { ReviewsProof } from "@/components/reviews-proof";
import { LOCAL_BUSINESS_SCHEMA, ORGANIZATION_SCHEMA } from "@/lib/schema-org";
const karaokeHero = karaokeHeroAsset.url;

const CANONICAL = "https://dj-val.fr/location-karaoke-vaucluse";

const INCLUDED = [
  "Système son complet + câblage — qualité vocale calibrée pour le chant",
  "Vidéoprojecteur",
  "Écran de projection sur pied",
  "2 micros professionnels sans fil",
  "Identifiants d'un logiciel de karaoké en ligne avec catalogue de chansons illimité",
  "Ordinateur portable à prévoir de votre côté",
];

const ZONES = [
  { name: "Avignon", detail: "Livraison et installation possibles en option, ou retrait à l'atelier de Cavaillon (25 min)." },
  { name: "Cavaillon", detail: "Retrait gratuit sur rendez-vous, pack prêt à brancher : installation facile en quelques minutes." },
  { name: "Sorgues", detail: "Livraison rapide depuis Cavaillon, idéale pour une soirée d'entreprise ou un anniversaire." },
  { name: "Villeneuve-lès-Avignon", detail: "Livraison sur devis selon la distance, installation sur place en option." },
  { name: "Carpentras & Orange", detail: "Livraison sur devis selon la distance." },
  { name: "Luberon & Alpilles", detail: "Mas, gîtes et salles privées : installation en option." },
];

const FAQ = [
  {
    q: "Combien coûte la location d'un karaoké à Avignon ?",
    a: "Le pack karaoké complet est à 200 €. Il comprend le système son, le vidéoprojecteur, l'écran de projection, deux micros sans fil et les identifiants d'accès au logiciel de karaoké en ligne. La livraison, l'installation et l'animation sont des options facturées séparément.",
  },
  {
    q: "Que dois-je fournir moi-même ?",
    a: "Uniquement un ordinateur portable. Vous vous y connectez au logiciel de karaoké avec les identifiants que je vous remets : les paroles s'affichent sur l'écran de projection et le son passe par le système fourni.",
  },
  {
    q: "Le pack est-il difficile à installer ?",
    a: "Non. Il est pré-réglé avant le départ de l'atelier et l'installation est facile : brancher le son, poser l'écran, connecter le vidéoprojecteur à l'ordinateur. Une notice illustrée est fournie, et je reste joignable par téléphone le soir même.",
  },
  {
    q: "Peut-on louer le karaoké pour tout un week-end ?",
    a: "Oui, le tarif week-end court du vendredi 17 h au lundi 10 h. Idéal pour un anniversaire, un EVJF/EVG ou une fête de famille sur deux jours.",
  },
  {
    q: "Proposez-vous une soirée karaoké animée ?",
    a: "Oui, en option : je reste sur place, je lance les titres, je gère la file d'attente au micro et je bascule en soirée dansante quand le karaoké s'essouffle. Tarif sur devis.",
  },
];

export const Route = createFileRoute("/location-karaoke-vaucluse")({
  head: () => ({
    meta: [
      { title: "Location Pack Karaoké Avignon & Vaucluse dès 200€ | DJ Val" },
      {
        name: "description",
        content:
          "Louez votre matériel de karaoké professionnel dès 200€ chez DJ Val. Système son puissant, 2 micros sans fil, écran et projecteur. Livraison Avignon et Vaucluse."
      },
      { property: "og:title", content: "Location Pack Karaoké Avignon & Vaucluse dès 200€ | DJ Val" },
      {
        property: "og:description",
        content:
          metaDesc("Louez votre matériel de karaoké professionnel dès 200€ chez DJ Val. Système son puissant, 2 micros sans fil, écran et projecteur. Livraison Avignon et Vaucluse.")
      },
      { property: "og:type", content: "website" },
      { property: "og:url", content: CANONICAL },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:title", content: "Location Pack Karaoké Avignon & Vaucluse dès 200€ | DJ Val" },
      {
        name: "twitter:description",
        content: "Pack karaoké professionnel dès 200 € : son, vidéoprojecteur, écran, 2 micros sans fil. Avignon, Cavaillon, Vaucluse.",
      },
    ],
    links: [{ rel: "canonical", href: CANONICAL }],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Product",
          name: "Location Pack Karaoké Vaucluse — DJ Val",
          description:
            "Pack karaoké professionnel clé en main : système son, vidéoprojecteur, écran de projection, 2 micros sans fil et catalogue de chansons en ligne. Location dès 200 € à Avignon, Cavaillon et dans le Vaucluse.",
          image: karaokeHero,
          category: "Location de matériel karaoké",
          brand: { "@type": "Brand", name: "DJ Val" },
          offers: {
            "@type": "Offer",
            priceCurrency: "EUR",
            price: "200.00",
            availability: "https://schema.org/InStock",
            seller: ORGANIZATION_SCHEMA,
            url: CANONICAL,
            shippingDetails: {
              "@type": "OfferShippingDetails",
              shippingRate: {
                "@type": "MonetaryAmount",
                value: "0",
                currency: "EUR",
              },
              shippingDestination: {
                "@type": "DefinedRegion",
                addressCountry: "FR",
                addressRegion: "Vaucluse",
              },
              deliveryTime: {
                "@type": "ShippingDeliveryTime",
                handlingTime: { "@type": "QuantitativeValue", minValue: 0, maxValue: 1, unitCode: "DAY" },
                transitTime: { "@type": "QuantitativeValue", minValue: 0, maxValue: 1, unitCode: "DAY" },
              },
            },
            hasMerchantReturnPolicy: {
              "@type": "MerchantReturnPolicy",
              url: "https://dj-val.fr/conditions-de-location",
              applicableCountry: "FR",
              returnPolicyCategory: "https://schema.org/MerchantReturnFiniteReturnWindow",
              merchantReturnDays: 30,
              returnMethod: "https://schema.org/ReturnInStore",
              returnFees: "https://schema.org/FreeReturn",
            },
          },
        }),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Service",
          name: "Location de pack karaoké à Avignon et en Vaucluse",
          serviceType: "Location de matériel karaoké",
          areaServed: ["Avignon", "Cavaillon", "Sorgues", "Villeneuve-lès-Avignon", "Vaucluse"],
          provider: { ...LOCAL_BUSINESS_SCHEMA },
          offers: {
            "@type": "Offer",
            priceCurrency: "EUR",
            price: "200.00",
            url: CANONICAL,
            availability: "https://schema.org/InStock",
          },
        }),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "FAQPage",
          mainEntity: FAQ.map((f) => ({
            "@type": "Question",
            name: f.q,
            acceptedAnswer: { "@type": "Answer", text: f.a },
          })),
        }),
      },
    ],
  }),
  component: Page,
});

function Page() {
  return (
    <div className="bg-background text-muted-foreground">
      <PageHero
        image={karaokeHero}
        eyebrow="Location karaoké — Vaucluse"
        title={
          <>
            Location de Pack Karaoké en <span className="italic">Vaucluse</span> (Avignon, Cavaillon)
          </>
        }
        subtitle="Pack karaoké complet dès 200 € : son, vidéoprojecteur, écran, 2 micros sans fil et accès au logiciel. Retrait à Cavaillon ou livraison en option."
        primary={{ label: "Réserver le pack", to: "/contactez-nous" }}
        secondary={{ label: "07 67 90 86 67", href: "tel:+33767908667" }}
      />
      <ReviewsProof />

      <section className="py-16 px-6 max-w-4xl mx-auto">
        <h2 className="font-serif-display text-3xl md:text-4xl text-foreground mb-6">
          Un pack karaoké professionnel clé en main pour vos soirées
        </h2>
        <div className="space-y-4 text-sm md:text-base leading-relaxed font-light">
          <p>
            Le pack karaoké de DJ Val réunit tout ce qu'il faut pour transformer une salle, un mas ou une terrasse en
            scène le temps d'une soirée. Il part de l'atelier de Cavaillon, à vingt-cinq minutes d'Avignon, déjà réglé
            et testé : vous branchez, vous connectez votre ordinateur portable au logiciel de karaoké en ligne avec les
            identifiants fournis, et les paroles s'affichent sur l'écran de projection.
          </p>
          <p>
            C'est la formule la plus économique pour un anniversaire, un EVJF, une fête de famille, une soirée
            d'entreprise ou une soirée associative : vous ne payez que le matériel. La livraison, l'installation sur
            place et l'animation par mes soins restent des options — jamais imposées dans le prix de base.
          </p>
          <p>
            Le catalogue de chansons du logiciel couvre des dizaines de milliers de titres, des classiques français aux
            nouveautés internationales, et la qualité vocale du système est calibrée pour le chant : les voix restent
            claires et puissantes, même sur les titres les plus ambitieux.
          </p>
        </div>
      </section>

      <section className="py-16 px-6 bg-foreground/[0.03]">
        <div className="max-w-4xl mx-auto">
          <h2 className="font-serif-display text-2xl md:text-3xl text-foreground mb-8">
            Le matériel inclus : son, image et micros sans fil — 200 €
          </h2>
          <ul className="grid sm:grid-cols-2 gap-3 text-sm font-light">
            {INCLUDED.map((i) => (
              <li key={i} className="flex gap-3">
                <span className="text-[var(--gold)] shrink-0">—</span>
                <span>{i}</span>
              </li>
            ))}
          </ul>
          <p className="mt-8 text-sm">
            Options sur devis : <span className="text-foreground">livraison + installation</span> et{" "}
            <span className="text-foreground">animation de la soirée karaoké par DJ Val</span>.
          </p>
        </div>
      </section>

      <section className="py-16 px-6 max-w-4xl mx-auto">
        <h2 className="font-serif-display text-2xl md:text-3xl text-foreground mb-8">
          Zone de livraison et d'installation : Avignon, Cavaillon et alentours
        </h2>
        <div className="grid sm:grid-cols-2 gap-6">
          {ZONES.map((z) => (
            <div key={z.name} className="border-l border-[var(--gold)]/40 pl-4">
              <h3 className="text-foreground text-base mb-1">Karaoké {z.name}</h3>
              <p className="text-sm font-light">{z.detail}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="py-16 px-6 bg-foreground/[0.03]">
        <div className="max-w-3xl mx-auto">
          <h2 className="font-serif-display text-2xl md:text-3xl text-foreground mb-8">Questions fréquentes</h2>
          <div className="space-y-6">
            {FAQ.map((f) => (
              <div key={f.q}>
                <h3 className="text-foreground text-base mb-2">{f.q}</h3>
                <p className="text-sm font-light leading-relaxed">{f.a}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-16 px-6 max-w-3xl mx-auto text-center">
        <h2 className="font-serif-display text-2xl md:text-3xl text-foreground mb-4">Bloquer une date pour le karaoké</h2>
        <p className="text-sm font-light mb-8">Réponse sous 24 h, devis ferme, aucun frais caché.</p>
        <div className="flex flex-wrap justify-center gap-4">
          <Link
            to="/contactez-nous"
            className="px-8 py-3 bg-[var(--gold)] text-[var(--ink)] text-sm tracking-wide hover:opacity-90 transition"
          >
            Demander un devis
          </Link>
          <a
            href="tel:+33767908667"
            className="px-8 py-3 border border-foreground/20 text-foreground text-sm tracking-wide hover:border-[var(--gold)] transition"
            style={{ fontFamily: "Arial, Helvetica, sans-serif" }}
          >
            07 67 90 86 67
          </a>
        </div>
        <p className="mt-10 text-xs">
          À voir aussi :{" "}
          <Link to="/location-materiel/$slug" params={{ slug: "karaoke" }} className="text-[var(--gold)] hover:underline">
            fiche complète du pack karaoké
          </Link>
          {" · "}
          <Link to="/location-photobooth-avignon" className="text-[var(--gold)] hover:underline">
            location photobooth Avignon
          </Link>
          {" · "}
          <Link to="/location-materiel" className="text-[var(--gold)] hover:underline">
            tout le matériel à louer
          </Link>
        </p>
      </section>
    </div>
  );
}
